import { makeAutoObservable } from "mobx"
import {
  DEFAULT_LOCALE,
  LOCALE_STORAGE_KEY,
  type Locale,
  type TranslationKey,
  isSupportedLocale,
  translate,
} from "../lib/i18n.js"
import { getTelegramWebApp } from "../lib/telegram.js"
import type { RootStore } from "./RootStore.js"

export class I18nStore {
  locale: Locale = DEFAULT_LOCALE
  rootStore: RootStore

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore
    makeAutoObservable(this, { rootStore: false })
    this.detectLocale()
  }

  /**
   * Detect locale with the following priority:
   * 1. localStorage override (user previously chose a language)
   * 2. Telegram user language_code from Mini App init data
   * 3. Default to "en"
   */
  detectLocale(): void {
    // 1. Check localStorage override
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY)
    if (stored && isSupportedLocale(stored)) {
      this.locale = stored
      return
    }

    // 2. Check Telegram user language_code
    const webapp = getTelegramWebApp()
    const langCode = webapp?.initDataUnsafe?.user?.language_code
    if (langCode && isSupportedLocale(langCode)) {
      this.locale = langCode
      return
    }

    // 3. Default
    this.locale = DEFAULT_LOCALE
  }

  setLocale(locale: Locale): void {
    this.locale = locale
    localStorage.setItem(LOCALE_STORAGE_KEY, locale)
  }

  /**
   * Translate a key using the current locale.
   * This is an observable getter — components wrapped with `observer`
   * will re-render when `this.locale` changes.
   */
  t(key: TranslationKey): string {
    return translate(this.locale, key)
  }
}
